import {LengthUnit} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {CssMixBlendModeValues, IVanillaReaderUiOptionsData, VanillaReaderUiActionNames} from './VanillaReaderUiModel';
import {Key} from "../utils/Key.enum";
import {VanillaReaderRendererNames} from "../VanillaReader/VanillaReaderModel";

/*
* # VanillaReaderAppUiDefaults
*
* ## RESPONSIBILITIES
*
* This class is used to hold the default values to for settings in the UI.
*
* */

export class VanillaReaderAppUiDefaults {
    static readonly highlightColorValues = {
        red: '#FF8080',
        green: '#AFE9DD',
        blue: '#80B3FF',
        yellow: '#FFDD55',
    };

    static readonly readingRulerColorValues = {
        transparent: 'transparent',
        red: '#ffabab',
        green: '#AFE9DD',
        blue: '#bbd7ff',
        yellow: '#ffe39a',
    };

    static readonly options: IVanillaReaderUiOptionsData = {
        publicationStyleOptions: {
            fontSizeScaleFactor: 1,
            lineHeightScaleFactor: 1.2,
            textAlignment: null,
            pageMargins: {
                top: {
                    unit: LengthUnit.PERCENT,
                    value: 6,
                },
                bottom: {
                    unit: LengthUnit.PERCENT,
                    value: 6,
                },
                left: {
                    unit: LengthUnit.PERCENT,
                    value: 8,
                },
                right: {
                    unit: LengthUnit.PERCENT,
                    value: 8,
                },
            },
        },
        dateCreated: undefined,
        showNarrationControls: false,
        useAccessibilityMode: false,
        ignoreAspectRatio: false,
        useReducedMotion: false,
        useDarkMode: false,
        activeRendererTypeName: VanillaReaderRendererNames.RESPONSIVE,
        activeUiPaletteName: 'Dark',
        activePublicationFontSetName: 'default',
        activePublicationPaletteName: 'default',
        mediaPlayerPlaybackRate: 1,
        readingRulerOptions: {
            lineBoxBorderTopWidth: 4,
            lineBoxBorderTopColor: 'rgba(0,0,0,0.6)',
            lineBoxBorderBottomWidth: 4,
            lineBoxBorderBottomColor: 'rgba(0,0,0,0.6)',
            lineBoxBorderLeftWidth: 0,
            lineBoxBorderLeftColor: 'transparent',
            lineBoxBorderRightWidth: 0,
            lineBoxBorderRightColor: 'transparent',

            lineBoxMixBlendMode: CssMixBlendModeValues.NORMAL,
            lineBoxBackgroundColor: '#ffabab',
            lineBoxOpacity: 1,
            lineBoxMinHeight: 22,
            lineBoxMaxHeight: 128,
            lineBoxLineSpan: 3,
            lineBoxLineVerticalSpacing: 1.5,

            overlayBackgroundColor: '#ffffff',
            overlayOpacity: 0.8,
            overlayMixBlendMode: CssMixBlendModeValues.NORMAL,
            overlayIsVisible: true,

            rulerBorderTopWidth: 48,
            rulerBorderTopColor: 'rgba(0,0,0,0.0)',
            rulerBorderBottomWidth: 48,
            rulerBorderBottomColor: 'rgba(0,0,0,0.0)',
            rulerBorderLeftWidth: 0,
            rulerBorderLeftColor: 'transparent',
            rulerBorderRightWidth: 0,
            rulerBorderRightColor: 'transparent',

            rulerBackgroundColor: 'transparent',
            rulerOpacity: 1,
            rulerMixBlendMode: CssMixBlendModeValues.MULTIPLY,
            rulerMinHeight: 128,
            rulerDragThreshold: 16,

            snapToLineBoxes: true,
        },
        showPageNumbers: true,
        scrollOverflowingTables: false,
        uIActionKeyCommands: [
            {
                actionName: VanillaReaderUiActionNames.NAVIGATE_NEXT,
                key: 'ArrowRight',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.NAVIGATE_PREVIOUS,
                key: 'ArrowLeft',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.NAVIGATE_NEXT,
                key: 'ArrowUp',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.NAVIGATE_PREVIOUS,
                key: 'ArrowDown',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.MAIN_MENU_OPEN,
                key: 'm',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.SEARCH_DIALOG_OPEN,
                key: 's',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.SEARCH_CLEAR,
                key: 'S',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.BOOKMARK_READING_POSITION,
                key: 'b',
                modifiers: [
                    Key.Alt
                ]
            },
            {
                actionName: VanillaReaderUiActionNames.BOOKMARK_CLIPBOARD,
                key: 'B',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.TOC_OPEN_DIALOG,
                key: 'c',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.SETTINGS_DIALOG_OPEN,
                key: 't',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.SETTINGS_RESET,
                key: 'T',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.BOOKSHELF_DIALOG_OPEN,
                key: 'q',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.CLOSE_DIALOGS_AND_FOCUS_ON_READING_POSITION,
                key: 'x',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.BOOKMARK_OPEN_DIALOG_ALL,
                key: 'o',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.BOOKMARK_OPEN_DIALOG_ACTIVE,
                key: 'O',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.HIGHLIGHT_OPEN_DIALOG_ALL,
                key: 'h',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.HIGHLIGHT_OPEN_DIALOG_ACTIVE,
                key: 'H',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.HELP_DIALOG_OPEN,
                key: 'F1',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.HELP_DIALOG_OPEN,
                key: '/',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.HIGHLIGHT_SELECTION,
                key: 'i',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.HIGHLIGHT_CLIPBOARD,
                key: 'I',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.WHERE_AM_I,
                key: 'w',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.NAVIGATE_NEXT_HEADING,
                key: 'g',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.NAVIGATE_PREVIOUS_HEADING,
                key: 'G',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.MEDIA_PLAYBACK_START,
                key: 'r',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.MEDIA_PLAYBACK_STOP,
                key: 'R',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.MEDIA_PLAYBACK_SKIP_FORWARD,
                key: 'k',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.MEDIA_PLAYBACK_SKIP_BACKWARD,
                key: 'K',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.MEDIA_PLAYBACK_ESCAPE_ACTIVE_SEGMENT,
                key: 'a',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.MEDIA_PLAYBACK_ESCAPE_ALL,
                key: 'A',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.ACCESSIBILITY_MODE_ENTER,
                key: 'z',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.ACCESSIBILITY_MODE_EXIT,
                key: 'Z',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.GOTO_PAGE_DIALOG_OPEN,
                key: 'p',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.ACTIONS_MENU_OPEN,
                key: 'F2',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.ZOOM_IN,
                key: '+',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.ZOOM_TOOL_ACTIVATE,
                key: '?',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.ZOOM_OUT,
                key: '-',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.ZOOM_TOOL_DEACTIVATE,
                key: '_',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.READING_RULER_ACTIVATE,
                key: 'u',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.READING_RULER_DEACTIVATE,
                key: 'U',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.READING_RULER_MOVE_PREVIOUS,
                key: '<',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.READING_RULER_MOVE_NEXT,
                key: '>',
                modifiers: [Key.Alt, Key.Shift]
            },
            {
                actionName: VanillaReaderUiActionNames.LANDMARKS_DIALOG_OPEN,
                key: 'l',
                modifiers: [Key.Alt]
            },
            {
                actionName: VanillaReaderUiActionNames.NAVIGATION_HISTORY_DIALOG_OPEN,
                key: 'y',
                modifiers: [Key.Alt]
            },
        ],
    };

}
